import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePricingNav } from "../../hooks/usePricingNav";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  bilingualSearchText,
  localizedDescription,
  localizedTitle,
} from "../../utils/localizedContent";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Grid,
  Skeleton,
  TextField,
  Typography,
  InputAdornment,
  Stack,
} from "@mui/material";
import {
  AccessTime,
  GroupsOutlined,
  LockOutlined,
  PersonOutlined,
  PlayCircleOutlined,
  Search,
  MenuBook,
} from "@mui/icons-material";
import {
  MEMBERSHIP_COLORS,
  ContentContainer,
  StyledTabs,
  StyledTab,
  Thumbnail,
  DurationTag,
} from "../../components/membership/MembershipStyles";
import { coursesService, PublicCourseCard } from "../../services/coursesService";

type SessionTab = "group" | "private";

const formatDuration = (seconds?: number): string => {
  if (!seconds) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const CourseCard: React.FC<{ course: PublicCourseCard; isLocked: boolean; onClick: () => void }> = ({ course, isLocked, onClick }) => {
  const { t, language } = useLanguage();
  const title = localizedTitle(course, language);
  const description = localizedDescription(course, language);
  return (
  <Box
    onClick={onClick}
    sx={{ cursor: "pointer", "&:hover .course-title": { color: MEMBERSHIP_COLORS.accent } }}
  >
    <Thumbnail>
      {course.thumbnail ? (
        <Box
          component="img"
          src={course.thumbnail}
          alt={title}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(216,154,122,0.1)",
          }}
        >
          <MenuBook sx={{ fontSize: 48, color: MEMBERSHIP_COLORS.accent, opacity: 0.5 }} />
        </Box>
      )}
      {course.estimatedDuration > 0 && (
        <DurationTag>
          <AccessTime sx={{ fontSize: 10, mr: 0.3 }} />
          {formatDuration(course.estimatedDuration)}
        </DurationTag>
      )}
      {isLocked && (
        <Box
          sx={{
            position: "absolute",
            bottom: 10,
            left: 10,
            zIndex: 4,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: "rgba(0,0,0,0.55)",
            borderRadius: "20px",
            px: 1.2,
            py: 0.4,
          }}
        >
          <LockOutlined sx={{ fontSize: 14, color: "white" }} />
          <Typography variant="caption" sx={{ color: "white", fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.05em" }}>
            LOCKED
          </Typography>
        </Box>
      )}
    </Thumbnail>
    <Typography
      className="course-title"
      variant="body2"
      sx={{
        fontWeight: 700,
        color: MEMBERSHIP_COLORS.text,
        fontSize: "0.95rem",
        lineHeight: 1.4,
        transition: "color 0.15s",
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography
        variant="caption"
        sx={{
          color: MEMBERSHIP_COLORS.secondary,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          mb: 0.5,
        }}
      >
        {description}
      </Typography>
    )}
    <Stack direction="row" spacing={1} mt={0.5}>
      <Chip
        icon={<MenuBook sx={{ fontSize: "0.7rem !important" }} />}
        label={t.classes.phasesCount(course.phaseCount)}
        size="small"
        sx={{ fontSize: "0.68rem", height: 20, bgcolor: "rgba(0,0,0,0.04)", color: MEMBERSHIP_COLORS.secondary }}
      />
      {course.videoCount > 0 && (
        <Chip
          icon={<PlayCircleOutlined sx={{ fontSize: "0.7rem !important" }} />}
          label={t.classes.videosCount(course.videoCount)}
          size="small"
          sx={{ fontSize: "0.68rem", height: 20, bgcolor: "rgba(0,0,0,0.04)", color: MEMBERSHIP_COLORS.secondary }}
        />
      )}
    </Stack>
  </Box>
  );
};

const SessionList: React.FC<{ sessionType: SessionTab; isLocked: boolean; onLockedClick: () => void }> = ({ sessionType, isLocked, onLockedClick }) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<PublicCourseCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await coursesService.getCourses({ sessionType, limit: 100 });
        if (!cancelled) setCourses(res.courses || []);
      } catch {
        if (!cancelled) setError("Failed to load sessions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sessionType]);

  const filtered = courses.filter((c) =>
    bilingualSearchText(c.title, c.titleAr, c.description, c.descriptionAr)
      .includes(search.toLowerCase())
  );

  return (
    <Box sx={{ mt: 3 }}>
      <TextField
        placeholder="Search sessions..."
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: MEMBERSHIP_COLORS.secondary, fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          width: { xs: "100%", sm: 320 },
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            bgcolor: "#FFFFFF",
          },
        }}
      />

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && (
        <Grid container spacing={3}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Skeleton variant="rounded" height={160} sx={{ borderRadius: "12px", mb: 1 }} />
              <Skeleton width="80%" height={20} />
              <Skeleton width="50%" height={16} />
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ py: 10, textAlign: "center" }}>
          {sessionType === "group" ? (
            <GroupsOutlined sx={{ fontSize: 56, color: MEMBERSHIP_COLORS.accent, mb: 2, opacity: 0.5 }} />
          ) : (
            <PersonOutlined sx={{ fontSize: 56, color: MEMBERSHIP_COLORS.accent, mb: 2, opacity: 0.5 }} />
          )}
          <Typography variant="h6" sx={{ color: MEMBERSHIP_COLORS.text, fontWeight: 700, mb: 1 }}>
            {search
              ? "No sessions match your search"
              : `No ${sessionType} sessions available yet`}
          </Typography>
          <Typography variant="body2" sx={{ color: MEMBERSHIP_COLORS.secondary }}>
            {search ? "Try a different search term." : "Check back soon for upcoming sessions."}
          </Typography>
        </Box>
      )}

      {!loading && filtered.length > 0 && (
        <>
          <Box sx={{ mb: 2 }}>
            <Chip
              label={`${filtered.length} session${filtered.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{ bgcolor: "rgba(216,154,122,0.15)", color: MEMBERSHIP_COLORS.accent, fontWeight: 700 }}
            />
          </Box>
          <Grid container spacing={3}>
            {filtered.map((course) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={course._id}>
                <CourseCard
                  course={course}
                  isLocked={isLocked}
                  onClick={isLocked ? onLockedClick : () => navigate(`/membership/classes/${course._id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

const Coaching: React.FC = () => {
  const [tab, setTab] = useState<SessionTab>("group");
  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const goToPricing = usePricingNav();

  const isAdmin = user?.type === "admin";
  const isLocked = !isAdmin && !user?.step1Completed;

  return (
    <ContentContainer maxWidth="xl">
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: MEMBERSHIP_COLORS.text,
            letterSpacing: "-0.03em",
            fontSize: { xs: "1.8rem", md: "2.4rem" },
            mb: 0.5,
          }}
        >
          Coaching
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: MEMBERSHIP_COLORS.secondary, fontSize: "1.05rem" }}
        >
          Personalized coaching sessions to accelerate your progress.
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid rgba(0,0,0,0.08)` }}>
        <StyledTabs
          value={tab}
          onChange={(_, v) => setTab(v as SessionTab)}
        >
          <StyledTab
            value="group"
            label={
              <Stack direction="row" alignItems="center" spacing={0.8}>
                <GroupsOutlined sx={{ fontSize: 18 }} />
                <span>Group Sessions</span>
              </Stack>
            }
          />
          <StyledTab
            value="private"
            label={
              <Stack direction="row" alignItems="center" spacing={0.8}>
                <PersonOutlined sx={{ fontSize: 18 }} />
                <span>Private Sessions</span>
              </Stack>
            }
          />
        </StyledTabs>
      </Box>

      <SessionList sessionType={tab} isLocked={isLocked} onLockedClick={() => setLockDialogOpen(true)} />

      {/* Step 1 required dialog */}
      <Dialog
        open={lockDialogOpen}
        onClose={() => setLockDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "20px", p: 1, maxWidth: 360, width: "100%" } }}
      >
        <DialogContent sx={{ textAlign: "center", py: 4, px: 3 }}>
          <LockOutlined sx={{ fontSize: 44, color: MEMBERSHIP_COLORS.accent, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: MEMBERSHIP_COLORS.text, mb: 1 }}>
            Step 1 Required
          </Typography>
          <Typography variant="body2" sx={{ color: MEMBERSHIP_COLORS.secondary, mb: 3 }}>
            Complete Step 1 of your membership to unlock coaching sessions.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setLockDialogOpen(false); goToPricing(); }}
              sx={{
                backgroundColor: MEMBERSHIP_COLORS.accent,
                color: "#fff",
                borderRadius: "24px",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: MEMBERSHIP_COLORS.primary },
              }}
            >
              Go to Step 1
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setLockDialogOpen(false)}
              sx={{ color: MEMBERSHIP_COLORS.secondary, textTransform: "none", fontWeight: 600 }}
            >
              Maybe later
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </ContentContainer>
  );
};

export default Coaching;
